import React, { useCallback, useContext, useEffect, useState } from "react";
import usePermissionsApi from "../../apiHooks/usePermissionsApi";
import { ROUTES } from "../navigation";
import { useDispatch } from "react-redux";
import useUsersApi from "../../apiHooks/useUsersApi";
import { REQUEST_STATUS } from "../../../utils/constants";
import { MODULE_KEYS } from "../../permissions/types/permission-types";

const AuthContext = React.createContext({
  allPermissions: [],
  isAuthorizedToAccess: undefined,
  isModuleEntitled: undefined,
  isPermissionLoading: true,
  getAllPermissions: undefined,
  getAllEntitledModules: undefined,
  isLoadingRoute: undefined,
  isPermissionError: false,
  isEntitledModulesError: false,
  reloadUserData: undefined,
  isEntitledModuleLoading: false,
});

export const useAuthContext = () => useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  const [allPermissions, setAllPermissions] = useState({
    appointment: [],
    patient: [],
    guardian: [],
    inventory: [],
    supplier: [],
    billing: [],
    dashboard: [],
    settings: [],
  });

  const path = window.location.pathname;

  const {
    common: { SET_LOGGED_USER },
  } = useDispatch();

  const {
    getCurrentUserApi: { get: fetchCurrentUser },
  } = useUsersApi();

  const {
    getAllPermissionsApi: {
      data,
      isLoading: isPermissionLoading,
      get: getAllPermissions,
      error,
    },
    getAllEntitledModulesApi: {
      data: entitledModulesData,
      isLoading: isEntitledModuleLoading,
      error: entitledModulesErr,
      get: getAllEntitledModules,
    },
  } = usePermissionsApi();

  const formatPermissions = useCallback(() => {
    if (data.length === 0) {
      return;
    }

    const groupedPermissions = data.reduce((acc, permission) => {
      const [module, action] = permission.split(":");
      if (!acc[module]) {
        acc[module] = [];
      }
      acc[module].push(action);
      return acc;
    }, {});

    setAllPermissions(groupedPermissions);
  }, [data]);

  const isAuthorizedToAccess = useCallback(
    (module, permission) => {
      const moduleArr = allPermissions[module];
      if (!moduleArr) return false;

      const moduleSet = new Set(moduleArr);
      return permission ? moduleSet.has(permission) : moduleArr.length > 0;
    },
    [allPermissions],
  );

  const isModuleEntitled = useCallback(
    (module) => {
      return entitledModulesData.includes(MODULE_KEYS[module]);
    },
    [entitledModulesData],
  );

  const isLoadingRoute = useCallback(() => {
    return path === "/" || path.includes(ROUTES.initializing);
  }, [path]);

  const handleAppUserConfig = useCallback(async () => {
    const userResponse = await fetchCurrentUser();
    if (userResponse.status === REQUEST_STATUS.success) {
      SET_LOGGED_USER(userResponse.data);
      getAllPermissions();
      getAllEntitledModules();
    }
  }, [
    SET_LOGGED_USER,
    fetchCurrentUser,
    getAllEntitledModules,
    getAllPermissions,
  ]);

  const reloadUserData = useCallback(async () => {
    const userResponse = await fetchCurrentUser();
    if (userResponse.status === REQUEST_STATUS.success) {
      SET_LOGGED_USER(userResponse.data);
    }
  }, [SET_LOGGED_USER, fetchCurrentUser]);

  useEffect(() => {
    formatPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    //all pages except the preloader
    if (!isLoadingRoute()) {
      handleAppUserConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return (
    <AuthContext.Provider
      value={{
        allPermissions,
        isAuthorizedToAccess,
        isPermissionLoading,
        getAllPermissions,
        getAllEntitledModules,
        isLoadingRoute,
        isPermissionError: error,
        isEntitledModulesError: entitledModulesErr,
        reloadUserData,
        isEntitledModuleLoading,
        isModuleEntitled,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
